<template>
  <v-navigation-drawer
    v-model="localValue"
    fixed
    right
    temporary
    width="700"
    class="member-drawer"
    style="z-index: 210"
  >
    <!-- Header -->
    <div class="member-drawer-header">
      <div class="member-drawer-header-top">
        <div class="member-drawer-header-info">
          <div class="member-name">{{ memberName }}</div>
          <div class="member-drawer-meta">
            <span v-if="member && (member.email || member.evcoId)" class="member-meta-item">
              <v-icon x-small color="#9ca3af" class="mr-1">mdi-card-account-details-outline</v-icon>{{ member.email || member.evcoId }}
            </span>
            <span v-if="member && member.company" class="member-meta-item">
              <v-icon x-small color="#9ca3af" class="mr-1">mdi-domain</v-icon>{{ member.company }}
            </span>
          </div>
        </div>
        <div class="member-drawer-header-actions">
          <v-btn v-if="memberDetailHref" icon small :href="memberDetailHref" target="_blank" :title="$t('org-dashboard.member-drawer-open-tab')">
            <v-icon small>mdi-open-in-new</v-icon>
          </v-btn>
          <v-btn icon small @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="member-drawer-loading">
      <v-progress-circular indeterminate color="#FF5722" size="32" />
    </div>

    <!-- Content -->
    <div v-else-if="data" class="member-drawer-content">
      <!-- KPIs -->
      <div class="drawer-section">
        <div class="drawer-kpi-grid">
          <div v-for="(kpi, i) in kpiItems" :key="i" class="drawer-kpi-card">
            <div class="drawer-kpi-value">{{ kpi.value }}</div>
            <div class="drawer-kpi-label">{{ kpi.label }}</div>
          </div>
        </div>
      </div>

      <!-- Split donut -->
      <div class="drawer-section" v-if="splitSegments.length > 0">
        <h4 class="drawer-section-title">{{ $t('org-dashboard.member-split') }}</h4>
        <div class="member-split-body">
          <highcharts v-if="splitChartOptions" :options="splitChartOptions" />
          <div class="member-split-legend">
            <div v-for="(seg, i) in splitSegments" :key="i" class="member-split-legend-item">
              <span class="member-split-dot" :style="{backgroundColor: seg.color}"></span>
              <span>{{ seg.label }} ({{ seg.sessions }})</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Trend sparkline -->
      <div class="drawer-section" v-if="data.trend && data.trend.length">
        <h4 class="drawer-section-title">{{ $t('org-dashboard.member-trend') }}</h4>
        <highcharts v-if="trendChartOptions" :options="trendChartOptions" />
      </div>

      <!-- Heatmap -->
      <div class="drawer-section" v-if="heatmapRows.length > 0">
        <h4 class="drawer-section-title">{{ $t('org-dashboard.member-heatmap') }}</h4>
        <div class="drawer-heatmap">
          <div class="heatmap-row" v-for="(row, ri) in heatmapRows" :key="ri">
            <div class="heatmap-day">{{ row.day }}</div>
            <div class="heatmap-cells">
              <div
                v-for="(cell, ci) in row.hours"
                :key="ci"
                class="heatmap-cell"
                :style="{ backgroundColor: getHeatColor(cell) }"
                :title="`${row.day} ${ci}h: ${cell.sessions || 0}`"
              />
            </div>
          </div>
          <div class="heatmap-hours-row">
            <div class="heatmap-day" />
            <div class="heatmap-cells">
              <span v-for="h in [0, 6, 12, 18, 23]" :key="h" class="heatmap-hour-label" :style="{ gridColumn: h + 1 }">{{ h }}h</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Eco impact card -->
      <div class="drawer-section" v-if="data.ecoImpact">
        <h4 class="drawer-section-title">{{ $t('org-dashboard.member-eco-impact') }}</h4>
        <div class="member-eco-card">
          <div class="member-eco-item">
            <v-icon small color="#059669">mdi-leaf</v-icon>
            <span class="member-eco-value">{{ formatDecimal((data.ecoImpact.co2SavedKg || 0) / 1000) }} t CO₂</span>
          </div>
          <div class="member-eco-item">
            <v-icon small color="#6B7280">mdi-car</v-icon>
            <span class="member-eco-value">{{ formatNumber(data.ecoImpact.estimatedKm) }} km</span>
          </div>
          <div class="member-eco-item">
            <v-icon small color="#059669">mdi-tree</v-icon>
            <span class="member-eco-value">{{ formatNumber(data.ecoImpact.treesEquivalent) }} {{ $t('org-dashboard.eco-trees-equiv') }}</span>
          </div>
        </div>
      </div>

      <!-- Fleet Position -->
      <div class="drawer-section" v-if="fleetPosition">
        <h4 class="drawer-section-title">
          <v-icon small color="#8B5CF6" class="mr-1">mdi-chart-bar</v-icon>
          {{ $t('org-dashboard.member-fleet-position') }}
        </h4>
        <div class="fleet-position-card">
          <div class="fleet-position-value">{{ formatFleetValue(fleetPosition.value) }} {{ fleetPositionUnit }}</div>
          <div class="fleet-position-rank">{{ $t('org-dashboard.member-fleet-rank', { rank: fleetPosition.rank, total: fleetPosition.totalDrivers }) }}</div>
          <div class="fleet-position-percentile">{{ $t('org-dashboard.member-fleet-percentile', { pct: fleetPosition.percentileRank }) }}</div>
          <div class="fleet-position-bar-bg">
            <div class="fleet-position-bar-fill" :style="{ width: fleetPosition.percentileRank + '%', backgroundColor: fleetPositionColor }"></div>
          </div>
          <div class="fleet-position-pct-label">{{ fleetPosition.percentileRank }}%</div>
        </div>
      </div>

      <!-- Home refund badge -->
      <div class="drawer-section" v-if="data.homeRefund && data.homeRefund > 0">
        <div class="member-home-refund-badge">
          <v-icon small color="#3B82F6">mdi-home</v-icon>
          {{ $t('org-dashboard.member-home-refund') }}: {{ formatCurrency(data.homeRefund) }}
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="member-drawer-empty">
      <v-icon large color="grey lighten-1">mdi-information-outline</v-icon>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { Chart } from 'highcharts-vue'

const SEGMENT_COLORS = {
  Enterprise: '#3B82F6',
  Home: '#10B981',
  RoamingIncoming: '#8B5CF6',
  RoamingOutgoing: '#F59E0B',
}

const SEGMENT_LABEL_KEYS = {
  Enterprise: 'segment-enterprise',
  Home: 'segment-home',
  RoamingIncoming: 'segment-roaming-incoming',
  RoamingOutgoing: 'segment-roaming-outgoing',
}

const HEATMAP_COLORS = ['#FFF7ED', '#FDBA74', '#FB923C', '#EA580C', '#B91C1C']

export default {
  name: 'DashboardMemberDrawer',
  components: {
    highcharts: Chart,
  },
  props: {
    value: { type: Boolean, default: false },
    data: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    organizationId: { type: String, default: '' },
    memberId: { type: String, default: '' },
  },

  computed: {
    localValue: {
      get() { return this.value },
      set(val) { this.$emit('input', val) },
    },

    member() {
      return this.data?.member || this.data || null
    },

    memberName() {
      if (!this.member) return ''
      return this.member.name || this.member.fullName || [this.member.firstName, this.member.lastName].filter(Boolean).join(' ') || '-'
    },

    memberDetailHref() {
      const id = this.memberId || this.member?.id || this.member?.memberId
      if (!id) return null
      const route = this.$router.resolve({ name: 'member', params: { memberId: id } })
      return route.href
    },

    kpiItems() {
      const kpis = this.data?.kpis
      if (!kpis) return []
      return [
        { label: this.$t('org-dashboard.member-kpis-sessions'), value: kpis.sessions != null ? kpis.sessions.toLocaleString() : '-' },
        { label: this.$t('org-dashboard.member-kpis-energy'), value: kpis.energy != null ? Math.round(kpis.energy).toLocaleString() + ' kWh' : '-' },
        { label: this.$t('org-dashboard.member-kpis-cost'), value: kpis.cost != null ? this.formatCurrency(kpis.cost) : '-' },
        { label: this.$t('org-dashboard.member-kpis-avg-duration'), value: this.formatDuration(kpis.avgDuration) },
      ]
    },

    splitSegments() {
      const split = this.data?.split
      if (!split) return []
      // API returns split as object { SegmentName: { sessions, energy } }
      const entries = Array.isArray(split)
        ? split
        : Object.entries(split).map(([name, data]) => ({ name, ...data }))
      return entries
        .filter(s => (s.sessions || 0) > 0)
        .map(s => ({
          label: SEGMENT_LABEL_KEYS[s.name] ? this.$t('org-dashboard.' + SEGMENT_LABEL_KEYS[s.name]) : (s.name || s.label),
          sessions: s.sessions || 0,
          color: SEGMENT_COLORS[s.name] || '#6B7280',
        }))
    },

    splitChartOptions() {
      if (!this.splitSegments.length) return null

      return {
        chart: {
          type: 'pie',
          height: 160,
          width: 160,
          backgroundColor: 'transparent',
          margin: [0, 0, 0, 0],
        },
        title: { text: '' },
        credits: { enabled: false },
        tooltip: {
          pointFormat: '<b>{point.y}</b> ({point.percentage:.1f}%)',
        },
        plotOptions: {
          pie: {
            innerSize: '68%',
            dataLabels: { enabled: false },
            borderWidth: 0,
          },
        },
        series: [{
          data: this.splitSegments.map(s => ({
            name: s.label,
            y: s.sessions,
            color: s.color,
          })),
        }],
      }
    },

    trendChartOptions() {
      const trend = this.data?.trend
      if (!trend?.length) return null

      return {
        chart: {
          height: 180,
          backgroundColor: 'transparent',
          style: { fontFamily: 'inherit' },
          spacingTop: 10,
        },
        title: { text: null },
        credits: { enabled: false },
        legend: { enabled: true, itemStyle: { fontSize: '10px', fontWeight: '500' } },
        xAxis: {
          categories: trend.map(t => t.month || t.date || ''),
          labels: { style: { fontSize: '10px', color: '#9ca3af' } },
          gridLineWidth: 0,
          lineColor: '#e5e7eb',
        },
        yAxis: [
          {
            title: { text: null },
            labels: { style: { fontSize: '10px', color: '#6b7280' } },
            gridLineColor: '#f1f5f9',
            min: 0,
          },
          {
            title: { text: null },
            labels: { style: { fontSize: '10px', color: '#6b7280' }, format: '{value} kWh' },
            opposite: true,
            gridLineColor: 'transparent',
            min: 0,
          },
        ],
        tooltip: {
          shared: true,
          crosshairs: true,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 8,
          style: { fontSize: '11px' },
        },
        plotOptions: {
          spline: { lineWidth: 2, marker: { enabled: false } },
          series: { animation: { duration: 600 } },
        },
        series: [
          {
            name: this.$t('org-dashboard.member-kpis-sessions'),
            type: 'spline',
            color: '#3B82F6',
            yAxis: 0,
            data: trend.map(t => t.sessions || 0),
          },
          {
            name: this.$t('org-dashboard.member-kpis-energy'),
            type: 'spline',
            color: '#F59E0B',
            yAxis: 1,
            tooltip: { valueSuffix: ' kWh' },
            data: trend.map(t => Math.round(t.energy || 0)),
          },
        ],
      }
    },

    heatmapRows() {
      const raw = this.data?.heatmap
      if (!raw?.length) return []

      const dayKeys = ['day-mon', 'day-tue', 'day-wed', 'day-thu', 'day-fri', 'day-sat', 'day-sun']
      const tzOffset = Math.round(-new Date().getTimezoneOffset() / 60)

      const grouped = {}
      raw.forEach(entry => {
        let hour = entry.hour + tzOffset
        let day = entry.day
        if (hour >= 24) { hour -= 24; day = day < 7 ? day + 1 : 1 }
        else if (hour < 0) { hour += 24; day = day > 1 ? day - 1 : 7 }
        if (!grouped[day]) grouped[day] = new Array(24).fill(null).map(() => ({ sessions: 0 }))
        if (hour >= 0 && hour < 24) {
          grouped[day][hour] = { sessions: entry.sessions || entry.count || 0 }
        }
      })

      return [1, 2, 3, 4, 5, 6, 7].map(d => ({
        day: this.$t('org-dashboard.' + dayKeys[d - 1]),
        hours: grouped[d] || new Array(24).fill(null).map(() => ({ sessions: 0 })),
      }))
    },

    heatmapMax() {
      const raw = this.data?.heatmap
      if (!raw?.length) return 1
      return Math.max(...raw.map(r => r.sessions || r.count || 0), 1)
    },

    fleetPosition() {
      return this.data?.fleetPosition || null
    },

    fleetPositionUnit() {
      const metric = this.fleetPosition?.metric
      if (metric === 'cost') return '€'
      if (metric === 'sessions') return 'sessions'
      return 'kWh'
    },

    fleetPositionColor() {
      const pct = this.fleetPosition?.percentileRank ?? 0
      if (pct < 50) return '#10B981'
      if (pct <= 80) return '#F59E0B'
      return '#EF4444'
    },
  },

  methods: {
    close() {
      this.localValue = false
    },

    getHeatColor(cell) {
      const val = (cell.sessions || 0) / this.heatmapMax
      if (val === 0) return HEATMAP_COLORS[0]
      const idx = Math.min(Math.floor(val * (HEATMAP_COLORS.length - 1)) + 1, HEATMAP_COLORS.length - 1)
      return HEATMAP_COLORS[idx]
    },

    formatDuration(seconds) {
      if (seconds == null) return '-'
      // API sends duration in seconds
      const h = Math.floor(seconds / 3600)
      const m = Math.floor((seconds % 3600) / 60)
      if (h > 0) return `${h}h${String(m).padStart(2, '0')}`
      return `${m}m`
    },

    formatCurrency(val) {
      if (val == null) return '-'
      return new Intl.NumberFormat(this.$i18n.locale, { style: 'currency', currency: 'EUR' }).format(val)
    },

    formatDecimal(val) {
      if (val == null) return '-'
      return val.toFixed(2)
    },

    formatNumber(val) {
      if (val == null) return '-'
      if (typeof val === 'number') return Math.round(val).toLocaleString()
      return val
    },

    formatFleetValue(val) {
      if (val == null) return '-'
      const metric = this.fleetPosition?.metric
      if (metric === 'cost') return this.formatCurrency(val)
      if (metric === 'sessions') return Math.round(val).toLocaleString()
      return val.toLocaleString(this.$i18n.locale, { maximumFractionDigits: 1 })
    },
  },
}
</script>

<style lang="scss" scoped>
.member-drawer {
  ::v-deep .v-navigation-drawer__content {
    overflow-y: auto;
  }
}

.member-drawer-header {
  border-bottom: 1px solid #e5e7eb;
  padding: 14px 16px;
}

.member-drawer-header-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.member-drawer-header-info {
  flex: 1;
  min-width: 0;
}

.member-name {
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
}

.member-drawer-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 6px;
}

.member-meta-item {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  color: #6b7280;
}

.member-drawer-header-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.member-drawer-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
}

.member-drawer-content {
  padding: 16px 20px;
}

.member-drawer-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
}

/* Sections */
.drawer-section {
  margin-bottom: 24px;
}

.drawer-section-title {
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  margin-bottom: 12px;
}

/* KPIs */
.drawer-kpi-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.drawer-kpi-card {
  background: #f9fafb;
  border-radius: 8px;
  padding: 12px;
  border: 1px solid #f3f4f6;
  text-align: center;
}

.drawer-kpi-value {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.3;
}

.drawer-kpi-label {
  font-size: 10px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-top: 2px;
}

/* Split donut */
.member-split-body {
  display: flex;
  align-items: center;
  gap: 20px;
}

.member-split-legend {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.member-split-legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #4b5563;
}

.member-split-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* Heatmap */
.drawer-heatmap {
  background: #f9fafb;
  border-radius: 8px;
  padding: 12px;
}

.heatmap-row {
  display: flex;
  align-items: center;
  margin-bottom: 2px;
}

.heatmap-day {
  width: 32px;
  font-size: 10px;
  font-weight: 600;
  color: #6b7280;
  flex-shrink: 0;
}

.heatmap-cells {
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  gap: 1px;
  flex: 1;
}

.heatmap-cell {
  aspect-ratio: 1;
  border-radius: 2px;
  min-height: 12px;
}

.heatmap-hours-row {
  display: flex;
  align-items: center;
  margin-top: 4px;
}

.heatmap-hours-row .heatmap-cells {
  position: relative;
}

.heatmap-hour-label {
  font-size: 9px;
  color: #9ca3af;
}

/* Eco card */
.member-eco-card {
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.member-eco-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.member-eco-value {
  font-size: 13px;
  font-weight: 600;
  color: #065f46;
}

/* Fleet Position */
.fleet-position-card {
  background: #f5f3ff;
  border: 1px solid #ddd6fe;
  border-radius: 10px;
  padding: 16px;
}

.fleet-position-value {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 4px;
}

.fleet-position-rank {
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
  margin-bottom: 2px;
}

.fleet-position-percentile {
  font-size: 11px;
  color: #6b7280;
  margin-bottom: 10px;
}

.fleet-position-bar-bg {
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 99px;
  overflow: hidden;
  margin-bottom: 4px;
}

.fleet-position-bar-fill {
  height: 100%;
  border-radius: 99px;
  transition: width 0.6s ease;
}

.fleet-position-pct-label {
  font-size: 11px;
  font-weight: 700;
  color: #8B5CF6;
  text-align: right;
}

/* Home refund badge */
.member-home-refund-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #1e40af;
}
</style>
